import { FormlyFieldConfig } from 'node_modules/@ngx-formly/core/lib/models/fieldconfig';
import { Observable, Subject, tap } from 'rxjs';
import { Cacheable } from 'ts-cacheable';

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';

import { ToastService } from '@mitsubishi/core';
import { ApiUrlInjectionToken, UnitPerformanceDataResponse } from '@mitsubishi/shared/data-access';

import {
  CreateSystemUsagePlanPresetRequest,
  SystemUsagePlanPresetRequest,
  SystemUsagePlanPresetResponse,
  UpdateSystemUsagePlanPresetRequest,
} from '../models/energy-analysis-preset.model';
import {
  CreateEnergyAnalysisRulesRequest,
  EnergyAnalysisRulesRequest,
  EnergyAnalysisRulesResponse,
  UpdateEnergyAnalysisRulesRequest,
} from '../models/energy-analysis-rules.model';
import {
  CreateSystemRequest,
  CreateSystemUnitRequest,
  ExportSettingsResponse,
  System,
  SystemComparisonSettingsResponse,
  SystemRequest,
  SystemResponse,
  SystemUnitRequest,
  SystemUnitResponse,
  UpdateExportSettingsRequest,
  UpdateSystemRequest,
  UpdateSystemUnitRequest,
  UpdateSystemsComparisonSettingsRequest,
  UpdateWorkingConditionsRequest,
} from '../models/energy-analysis-system.model';
import {
  CreateEnergyAnalysisTaskRequest,
  LatestSystemsEnergyAnalysisTasksResponse,
  Task,
} from '../models/energy-analysis-task.model';
import {
  CapacityCurveDataResponse,
  CitiesList,
  CitiesRequest,
  ClimateData,
  ClimateDataRequest,
  CreateEnergyAnalysisRequest,
  EnergyAnalysis,
  EnergyAnalysisConfig,
  EnergyAnalysisListRequest,
  EnergyAnalysisListResponse,
  SystemUsagePlanBinsTableResponse,
  SystemUsagePlanGrid,
  UpdateCapacityCurve,
  UpdateEnergyAnalysisRequest,
  UpdateManualBinsTable,
} from '../models/energy-analysis.model';

export type EnergyAnalysisTableActions = 'upload' | 'download';

@Injectable({
  providedIn: 'root',
})
export class EnergyAnalysisService {
  http = inject(HttpClient);
  toastService = inject(ToastService);

  private tableActionsSubject = new Subject<EnergyAnalysisTableActions>();
  private readonly baseUrl: string;

  tableActions$ = this.tableActionsSubject.asObservable();

  constructor(@Inject(ApiUrlInjectionToken) apiServerUrl: string) {
    this.baseUrl = `${apiServerUrl}`;
  }
  performEnergyAnalysisTableAction(event: EnergyAnalysisTableActions) {
    this.tableActionsSubject.next(event);
  }

  // energy analysis
  searchEnergyAnalyses(energyAnalysisListRequest: EnergyAnalysisListRequest): Observable<any> {
    return this.http.get<EnergyAnalysisListResponse>(`${this.baseUrl}/energy-analysis`, {
      params: {
        ...energyAnalysisListRequest,
      },
    });
  }

  getEnergyAnalysis(energyAnalysisId: string): Observable<EnergyAnalysis> {
    return this.http.get<EnergyAnalysis>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}`);
  }

  createEnergyAnalysis(createEnergyAnalysisRequest: CreateEnergyAnalysisRequest): Observable<EnergyAnalysis> {
    return this.http.post<EnergyAnalysis>(`${this.baseUrl}/energy-analysis`, createEnergyAnalysisRequest);
  }

  updateEnergyAnalysis(
    energyAnalysisId: string,
    createEnergyAnalysisRequest: UpdateEnergyAnalysisRequest
  ): Observable<any> {
    return this.http.patch<EnergyAnalysis>(
      `${this.baseUrl}/energy-analysis/${energyAnalysisId}`,
      createEnergyAnalysisRequest
    );
  }

  deleteEnergyAnalysis(energyAnalysisId: string): Observable<any> {
    return this.http
      .delete<any>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}`)
      .pipe(tap(() => this.toastService.showToast({ resourceId: '451613' })));
  }

  // system types
  getEnergySystemType(): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(`${this.baseUrl}/system-types`);
  }

  // system usage types
  getEnergySystemUsageType(): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(`${this.baseUrl}/system-usage-types`);
  }

  // cities
  @Cacheable({
    maxCacheCount: 500,
  })
  searchCities(citiesRequest: CitiesRequest): Observable<CitiesList> {
    return this.http.get<CitiesList>(`${this.baseUrl}/ea-cities`, {
      params: {
        ...citiesRequest,
      },
    });
  }

  // charts city values
  getClimateData(cityId: string, climateDataRequest: ClimateDataRequest): Observable<ClimateData> {
    return this.http.get<ClimateData>(`${this.baseUrl}/ea-cities/${cityId}/climate-data`, {
      params: {
        ...climateDataRequest,
      },
    });
  }

  // RULES
  searchRules(rulesRequest: EnergyAnalysisRulesRequest): Observable<EnergyAnalysisRulesResponse> {
    return this.http.get<EnergyAnalysisRulesResponse>(`${this.baseUrl}/ea-system-usage-plan-rules`, {
      params: {
        ...rulesRequest,
      },
    });
  }

  createEnergyAnalysisRules(createEnergyAnalysisRulesRequest: CreateEnergyAnalysisRulesRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/ea-system-usage-plan-rules`, createEnergyAnalysisRulesRequest);
  }

  updateEnergyAnalysisRules(
    energyAnalysisId: string,
    updateEnergyAnalysisRulesRequest: UpdateEnergyAnalysisRulesRequest
  ): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/ea-system-usage-plan-rules/${energyAnalysisId}`,
      updateEnergyAnalysisRulesRequest
    );
  }

  deleteEnergyAnalysisRules(ruleId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/ea-system-usage-plan-rules/${ruleId}`);
  }

  copyFromPreset(energyAnalysisId: string, presetId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/ea-system-usage-plan-rules/copy-from-preset`, {
      analysisId: energyAnalysisId,
      presetId,
    });
  }

  // SYSTEM USAGE PLAN GRID
  getSystemUsagePlanGrid(energyAnalisisId: string): Observable<SystemUsagePlanGrid> {
    return this.http.get<SystemUsagePlanGrid>(
      `${this.baseUrl}/energy-analysis/${energyAnalisisId}/system-usage-plan-grid`
    );
  }

  // SYSTEM USAGE PLAN HISTOGRAM
  getSystemUsagePlanHistogram(energyAnalysisId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}/system-usage-plan-histogram`);
  }

  // CAPACITY CURVE
  getEnergyAnalysisHeatingCapacityCurve(energyAnalysisId: string): Observable<CapacityCurveDataResponse> {
    return this.http.get<any>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}/heating-capacity-curve`);
  }

  getEnergyAnalysisCoolingCapacityCurve(energyAnalysisId: string): Observable<CapacityCurveDataResponse> {
    return this.http.get<any>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}/cooling-capacity-curve`);
  }

  exportEnergyAnalysisCapacityCurves(energyAnalysisId: string): Observable<Blob> {
    return this.http.get<Blob>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}/capacity-curves/export`, {
      responseType: 'blob' as 'json',
    });
  }

  importEnergyAnalysisCapacityCurves(energyAnalysisId: string, file: File): Observable<CapacityCurveDataResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.patch<CapacityCurveDataResponse>(
      `${this.baseUrl}/energy-analysis/${energyAnalysisId}/capacity-curves/import`,
      formData
    );
  }

  // SYSTEM USAGE PLAN BINS TABLE
  getEnergyAnalysisSystemUsagePlanBinsTable(energyAnalysisId: string): Observable<SystemUsagePlanBinsTableResponse> {
    return this.http.get<any>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}/climate-data-bins-table`);
  }

  // ENERGY ANALYSIS CONFIGURATION
  getEnergyAnalysisConfig(energyAnalysisId: string): Observable<EnergyAnalysisConfig> {
    return this.http.get<any>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}/config`);
  }

  updateEnergyAnalysisHeatingCapacityCurve(
    energyAnalysisId: string,
    updateCapacityCurve: UpdateCapacityCurve
  ): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/energy-analysis/${energyAnalysisId}/heating-capacity-curve/entries/${updateCapacityCurve.key}`,
      {
        value: updateCapacityCurve.value,
      }
    );
  }

  updateEnergyAnalysisCoolingCapacityCurve(
    energyAnalysisId: string,
    updateCapacityCurve: UpdateCapacityCurve
  ): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/energy-analysis/${energyAnalysisId}/cooling-capacity-curve/entries/${updateCapacityCurve.key}`,
      {
        value: updateCapacityCurve.value,
      }
    );
  }

  updateManualClimateDataBinsTable(
    energyAnalysisId: string,
    updateManualBinsTable: UpdateManualBinsTable[]
  ): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}/manual-climate-data-bins-table`, {
      rows: updateManualBinsTable,
    });
  }

  getSystemComparisonSettings(energyAnalysisId: string): Observable<SystemComparisonSettingsResponse> {
    return this.http.get<SystemComparisonSettingsResponse>(
      `${this.baseUrl}/energy-analysis/${energyAnalysisId}/systems-comparison-settings`
    );
  }

  updateSystemComparisonSettings(
    energyAnalysisId: string,
    settings: UpdateSystemsComparisonSettingsRequest
  ): Observable<SystemComparisonSettingsResponse> {
    return this.http.patch<SystemComparisonSettingsResponse>(
      `${this.baseUrl}/energy-analysis/${energyAnalysisId}/systems-comparison-settings`,
      settings
    );
  }

  getExportSettings(energyAnalysisId: string): Observable<ExportSettingsResponse> {
    return this.http.get<ExportSettingsResponse>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}/export-settings`);
  }

  updateExportSettings(
    energyAnalysisId: string,
    settings: UpdateExportSettingsRequest
  ): Observable<ExportSettingsResponse> {
    return this.http.patch<ExportSettingsResponse>(
      `${this.baseUrl}/energy-analysis/${energyAnalysisId}/export-settings`,
      settings
    );
  }

  getEnergyAnalysisExportPdf(energyAnalysisId: string): Observable<Blob> {
    return this.http.get<Blob>(`${this.baseUrl}/energy-analysis/${energyAnalysisId}/exports/pdf`, {
      responseType: 'blob' as 'json',
    });
  }

  // PRESET
  searchSystemUsagePlanPreset(
    systemUsagePlanPresetRequest: SystemUsagePlanPresetRequest
  ): Observable<SystemUsagePlanPresetResponse> {
    return this.http.get<SystemUsagePlanPresetResponse>(`${this.baseUrl}/ea-system-usage-plan-presets`, {
      params: {
        ...systemUsagePlanPresetRequest,
      },
    });
  }

  createSystemUsagePlanPreset(createSystemUsagePlanPresetRequest: CreateSystemUsagePlanPresetRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/ea-system-usage-plan-presets`, createSystemUsagePlanPresetRequest);
  }

  updateSystemUsagePlanPreset(
    presetId: string,
    updateSystemUsagePlanPresetRequest: UpdateSystemUsagePlanPresetRequest
  ): Observable<any> {
    return this.http.patch<any>(
      `${this.baseUrl}/ea-system-usage-plan-presets/${presetId}`,
      updateSystemUsagePlanPresetRequest
    );
  }

  deleteSystemUsagePlanPreset(presetId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/ea-system-usage-plan-presets/${presetId}`);
  }

  // system
  searchSystem(systemRequest: SystemRequest): Observable<SystemResponse> {
    return this.http.get<SystemResponse>(`${this.baseUrl}/ea-systems`, {
      params: {
        ...systemRequest,
      },
    });
  }

  getSystem(systemId: string): Observable<System> {
    return this.http.get<System>(`${this.baseUrl}/ea-systems/${systemId}`);
  }

  createSystem(createSystemRequest: CreateSystemRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/ea-systems`, createSystemRequest);
  }

  updateSystem(systemId: string, updateSystemRequest: UpdateSystemRequest): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/ea-systems/${systemId}`, updateSystemRequest);
  }

  deleteSystem(systemId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/ea-systems/${systemId}`);
  }

  getLatestSystemsEnergyAnalysisTasks(systemsIds: string[]): Observable<LatestSystemsEnergyAnalysisTasksResponse> {
    return this.http.get<LatestSystemsEnergyAnalysisTasksResponse>(
      `${this.baseUrl}/ea-systems/analysis-results/latest`,
      {
        params: {
          systemsIds: systemsIds.join(','),
        },
      }
    );
  }

  getSourceSideHydronicKitsManagement(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ea-source-side-hydronic-kits-management`);
  }

  getUserSideHydronicKitsManagement(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/ea-user-side-hydronic-kits-management`);
  }

  // getBoilerElcaWorkingContitionsForm(systemId: string): Observable<FormlyFieldConfig[]> {
  //   return this.http.get<FormlyFieldConfig[]>(`${this.baseUrl}/ea-systems/${systemId}/boiler-working-conditions/form`);
  // }

  // updateBoilerElcaWorkingConditions(
  //   systemId: string,
  //   updateWorkingConditionsRequest: UpdateWorkingConditionsRequest
  // ): Observable<any> {
  //   return this.http.post<any>(
  //     `${this.baseUrl}/ea-systems/${systemId}/boiler-working-conditions`,
  //     updateWorkingConditionsRequest
  //   );
  // }

  // SYSTEM UNITS
  searchSystemUnit(systemUnitRequest: SystemUnitRequest): Observable<SystemUnitResponse> {
    return this.http.get<SystemResponse>(`${this.baseUrl}/ea-units`, {
      params: {
        ...systemUnitRequest,
      },
    });
  }

  getSystemUnit(systemUnitId: string): Observable<any> {
    return this.http.get<System>(`${this.baseUrl}/ea-units/${systemUnitId}`);
  }

  createSystemUnit(createSystemUnitRequest: CreateSystemUnitRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/ea-units`, createSystemUnitRequest);
  }

  updateSystemUnit(systemUnitId: string, updateSystemRequest: UpdateSystemUnitRequest): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/ea-units/${systemUnitId}`, updateSystemRequest);
  }

  deleteSystemUnit(systemUnitId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/ea-units/${systemUnitId}`);
  }

  getUnitWorkingConditionsForm(unitId: string): Observable<FormlyFieldConfig[]> {
    return this.http.get<FormlyFieldConfig[]>(`${this.baseUrl}/ea-units/${unitId}/working-conditions/form`);
  }

  getUnitPerformanceResult(unitId: string): Observable<UnitPerformanceDataResponse> {
    return this.http.get<UnitPerformanceDataResponse>(`${this.baseUrl}/ea-units/${unitId}/performance/result`);
  }

  updateUnitWorkingConditions(
    unitId: string,
    updateWorkingConditionsRequest: UpdateWorkingConditionsRequest
  ): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/ea-units/${unitId}/working-conditions`, updateWorkingConditionsRequest);
  }

  // TASK
  createEnergyAnalysisTask(createEnergyAnalysisTaskRequest: CreateEnergyAnalysisTaskRequest): Observable<Task> {
    return this.http.post<Task>(`${this.baseUrl}/energy-analysis-tasks`, createEnergyAnalysisTaskRequest);
  }

  // SYSTEM INFO
  getSystemWorkingMode(): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(`${this.baseUrl}/ea-system-working-modes`);
  }
  getSystemSourceSideExchangerFlowMode(): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(`${this.baseUrl}/ea-system-source-side-exchanger-flow-modes`);
  }
  getSystemUserSideExchangerFlowMode(): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(`${this.baseUrl}/ea-system-user-side-exchanger-flow-modes`);
  }
}
